import React, { useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { useEditPunchSessionExit } from '@hooks/api/punchSessionAdjustment';
import { PunchSessionAdjustment } from '@models/punchSessionAdjustment.model';

interface EditSessionExitModalProps {
  isOpen: boolean;
  onClose: () => void;
  enterPunchClockId: string;
  /** Current effective entry (correction if edited, else raw ENTER). */
  currentEnter: Date;
  /** Raw ENTER time — read-only reference. */
  rawEnter: Date;
  /** Current effective exit (correction if edited, else raw exit, else now). */
  currentExit: Date;
  /** Raw paired exit; null for an open session. Submitted as originalExitTime. */
  rawExit: Date | null;
  isOpenSession: boolean;
  locationName: string;
  /** Session's entry day — surfaced in the modal subtitle. */
  sessionDate: Date;
  /** Existing correction, if this session was edited before — shows its history. */
  existingAdjustment?: PunchSessionAdjustment | null;
}

// Minimum trimmed length for the mandatory reason.
const MIN_REASON_LENGTH = 5;

// datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL wall-clock time (no offset).
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

// Parse a datetime-local value into an explicit LOCAL Date. We build it from
// components rather than `new Date(str)` because a bare "YYYY-MM-DDTHH:mm"
// string has historically been parsed inconsistently across engines (some as
// UTC). Explicit component construction is unambiguously local everywhere.
const parseLocalInput = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
};

const formatDuration = (mins: number): string => {
  const total = Math.max(0, Math.floor(mins));
  if (total < 1) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatWhen = (value: string | Date): string => {
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// "Tue, Jul 22" — the session's day, shown alongside the location in the subtitle.
const formatSubtitleDate = (d: Date): string =>
  d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

const actorName = (
  user?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null,
): string => {
  if (!user) return 'Someone';
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.email || 'Someone';
};

export const EditSessionExitModal: React.FC<EditSessionExitModalProps> = ({
  isOpen,
  onClose,
  enterPunchClockId,
  currentEnter,
  rawEnter,
  currentExit,
  rawExit,
  isOpenSession,
  locationName,
  sessionDate,
  existingAdjustment,
}) => {
  const dispatch = useAppDispatch();
  const { mutateAsync, isPending } = useEditPunchSessionExit();

  const [enterValue, setEnterValue] = useState<string>(() => toLocalInputValue(currentEnter));
  // For an open session the effective exit is a stale "now" captured upstream;
  // seed the field from a fresh now so closing it records the present time,
  // never app-open time.
  const [exitValue, setExitValue] = useState<string>(() =>
    toLocalInputValue(isOpenSession ? new Date() : currentExit),
  );
  const [reason, setReason] = useState<string>('');

  // An exit can't be in the future — clamp the picker to now.
  const maxExitValue = toLocalInputValue(new Date());

  const parsedEnter = useMemo(() => parseLocalInput(enterValue), [enterValue]);
  const parsedExit = useMemo(() => parseLocalInput(exitValue), [exitValue]);

  const orderValid =
    !!parsedEnter && !!parsedExit && parsedExit.getTime() > parsedEnter.getTime();
  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;
  const canSave = orderValid && reasonValid && !isPending;

  const title = isOpenSession ? 'Close session' : 'Edit session hours';
  const subtitle = `${locationName} · ${formatSubtitleDate(sessionDate)}`;

  const resultingMinutes =
    orderValid && parsedEnter && parsedExit
      ? (parsedExit.getTime() - parsedEnter.getTime()) / 60000
      : 0;
  const originalMinutes = rawExit ? (rawExit.getTime() - rawEnter.getTime()) / 60000 : null;

  const handleConfirm = async (): Promise<void> => {
    if (!orderValid || !reasonValid || !parsedEnter || !parsedExit) return;
    try {
      await mutateAsync({
        enterPunchClockId,
        editedEnterTime: parsedEnter.toISOString(),
        editedExitTime: parsedExit.toISOString(),
        originalExitTime: rawExit ? rawExit.toISOString() : null,
        reason: trimmedReason,
      });
      dispatch(
        appendActionMessage({
          type: SnackType.SUCCESS,
          message: 'Session hours updated',
        }),
      );
      onClose();
    } catch (error) {
      dispatch(
        appendActionMessage({
          type: SnackType.ERROR,
          message: 'Failed to update session hours',
        }),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={subtitle}
      size="small"
      cancelText="Cancel"
      confirmText="Save"
      handleConfirm={handleConfirm}
      isLoading={isPending}
      disableConfirm={!canSave}
    >
      <div className="flex flex-col gap-4">
        {/* Open sessions are being closed by this edit — call it out up top. */}
        {isOpenSession && (
          <div className="bg-emerald-50 ring-1 ring-emerald-100 rounded-lg p-3 text-xs font-medium text-emerald-800">
            This session is still open — saving will close it.
          </div>
        )}

        {/* Reference: the raw session, read-only. */}
        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Original entry</span>
            <span className="font-medium text-gray-800">{formatWhen(rawEnter)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Original exit</span>
            <span className="font-medium text-gray-800">
              {rawExit ? formatWhen(rawExit) : 'Open session (no exit)'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Original hours</span>
            <span className="font-medium text-gray-800">
              {originalMinutes != null ? formatDuration(originalMinutes) : '—'}
            </span>
          </div>
        </div>

        <FormGroup label="Entry" htmlFor="corrected-enter">
          <Input
            id="corrected-enter"
            type="datetime-local"
            value={enterValue}
            max={exitValue}
            error={!orderValid}
            onChange={(e) => setEnterValue(e.target.value)}
          />
        </FormGroup>

        <FormGroup
          label={isOpenSession ? 'Set exit time' : 'Exit'}
          htmlFor="corrected-exit"
        >
          <Input
            id="corrected-exit"
            type="datetime-local"
            value={exitValue}
            min={enterValue}
            max={maxExitValue}
            error={!orderValid}
            onChange={(e) => setExitValue(e.target.value)}
          />
        </FormGroup>

        {/* Single validation message for the ordering constraint. */}
        {!orderValid && (
          <p className="-mt-2 text-xs text-red-600">
            The exit must be after the entry.
          </p>
        )}

        {/* Live readout so the manager sees the impact before saving. */}
        <div className="bg-indigo-50 ring-1 ring-indigo-100 rounded-lg p-3 flex items-center justify-between">
          <span className="text-[11px] font-medium text-indigo-700/80 uppercase tracking-wide">
            Resulting hours
          </span>
          <span className="text-lg font-semibold text-indigo-900">
            {formatDuration(resultingMinutes)}
          </span>
        </div>

        <FormGroup label="Reason" htmlFor="edit-reason">
          <TextareaInput
            id="edit-reason"
            value={reason}
            rows={2}
            maxLength={1000}
            placeholder="Why is this session being corrected?"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormGroup>
        {!reasonValid && (
          <p className="-mt-2 text-xs text-gray-500">
            A reason is required (minimum {MIN_REASON_LENGTH} characters).
          </p>
        )}

        {/* Append-only audit trail for prior corrections. */}
        {existingAdjustment?.history && existingAdjustment.history.length > 0 && (
          <div className="border-t border-gray-200 pt-3">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Edit history
            </p>
            <div className="flex flex-col gap-2">
              {existingAdjustment.history.map((h) => (
                <div
                  key={h.punchSessionAdjustmentHistoryId}
                  className="text-xs text-gray-600 bg-gray-50 rounded-md p-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-800 truncate">
                      {actorName(h.actorUser)}
                    </span>
                    <span className="text-gray-400 flex-shrink-0">{formatWhen(h.createdAt)}</span>
                  </div>
                  <div className="mt-0.5">
                    Entry: {h.previousEnterTime ? formatWhen(h.previousEnterTime) : '—'} →{' '}
                    {formatWhen(h.newEnterTime)}
                  </div>
                  <div className="mt-0.5">
                    Exit: {h.previousExitTime ? formatWhen(h.previousExitTime) : 'Open'} →{' '}
                    {formatWhen(h.newExitTime)}
                  </div>
                  {h.reason && <div className="mt-0.5 italic text-gray-500">“{h.reason}”</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditSessionExitModal;
